import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom"; // Added useNavigate for redirection
import axios from "axios";
import Sidebar from "../components/Sidebar";
import DashboardHeader from "../components/DashboardHeader";


const BASE_URL = process.env.REACT_APP_API_BASE_URL

const QuizAttemptsPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [userData, setUserData] = useState({
    name: "Loading...",
    initials: "--",
    notificationCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);

  const navigate = useNavigate();

  // Check for token existence
  const getAccessToken = () => {
    const token = localStorage.getItem("access");
    if (!token) {
      setError("You are not logged in. Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000); // Redirect after 2 seconds
      return null;
    }
    return token;
  };

  // Fetch profile photo separately
    useEffect(() => {
      const fetchUserProfile = async () => {
        try {
          const token = localStorage.getItem("access");
          const res = await axios.get(`${BASE_URL}/users/profile/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const p = res.data.profile_photo;
          if (p) {
            setProfilePhoto(
              p.startsWith("http") ? p : `${BASE_URL}${p}`
            );
          } else {
            setProfilePhoto(null);
          }
        } catch (e) {
          setProfilePhoto(null);
        }
      };
      fetchUserProfile();
    }, []);

  // Fetch user data
  const fetchUserData = async () => {
    const token = getAccessToken();
    if (!token) return;

    try {
      const response = await axios.get(`${BASE_URL}/users/profile/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserData({
        name: `${response.data.first_name} ${response.data.last_name}`,
        initials: `${response.data.first_name[0]}${response.data.last_name[0]}`,
        notificationCount: response.data.notification_count || 0,
      });
    } catch (err) {
      //console.error("Failed to fetch user data:", err.response?.data || err.message);
      setError("Failed to fetch user profile. Please try again.");
    }
  };

  // Fetch quiz attempts
  const fetchQuizAttempts = async (page = 1) => {
    const token = getAccessToken();
    if (!token) return;

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/quiz/quiz-attempts/?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Log the response for debugging
      //console.log("Quiz attempts response:", response.data);

      // Check if response.data.results exists
      if (!response.data.results) {
        throw new Error("Unexpected response format: 'results' field missing.");
      }

      const attempts = response.data.results.map((attempt) => {
        const totalQuestions = attempt.quiz.questions.length;
        const correctAnswers = Math.round((attempt.score / 100) * totalQuestions);
        const incorrectAnswers = totalQuestions - correctAnswers;
        return {
          id: attempt.id,
          date: new Date(attempt.completed_at).toLocaleString(),
          quizName: attempt.quiz.title || attempt.quiz.lesson.title,
          student: userData.name,
          questions: totalQuestions,
          //totalMarks: totalQuestions,
          correctAnswers,
          incorrectAnswers,
          earnedMarks: correctAnswers,
          earnedPercentage: attempt.score,
          result: attempt.score >= attempt.quiz.passing_percentage ? "Pass" : "Fail",
        };
      });
      setQuizAttempts(attempts);
      setTotalPages(Math.ceil(response.data.count / 10)); // Assuming 10 items per page
      setError(null); // Clear any previous errors
    } catch (err) {
      //console.error("Failed to fetch quiz attempts:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        setError("Session expired. Redirecting to login...");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setError(`Failed to fetch quiz attempts: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
    fetchQuizAttempts(currentPage);
  }, [currentPage]);

  return (
    <div className="dashboard-container">
      <div className="d-flex">
        <Sidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}/>
        <div className="main-content flex-grow-1">
          <DashboardHeader user={userData} profilePhoto={profilePhoto} onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
          <div className="dashboard-content p-4">
            <h2 className="dashboard-title mb-4">My Quiz Attempts</h2>

            {loading ? (
              <div>Loading...</div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : (
              <div className="quiz-attempts-table">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Quiz Info</th>
                        <th>Question</th>
                        <th>Correct Answer</th>
                        <th>Incorrect Answer</th>
                        <th>Earned Marks</th>
                        <th>Result</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quizAttempts.map((attempt) => (
                        <tr key={attempt.id}>
                          <td>
                            <div className="quiz-info">
                              <div className="quiz-date mx-2">{attempt.date} </div>
                              <div className="quiz-name">
                                {attempt.quizName}
                                <i className="fas fa-info-circle text-muted ms-1"></i>
                              </div>
                              {/*<div className="student-name">Student: {attempt.student}</div>*/}
                            </div>
                          </td>
                          <td className="text-center">{attempt.questions}</td>
                          <td className="text-center">{attempt.correctAnswers}</td>
                          <td className="text-center">{attempt.incorrectAnswers}</td>
                          <td className="text-center">
                            {attempt.earnedMarks} ({attempt.earnedPercentage.toFixed(2)}%)
                          </td>
                          <td className="text-center">
                            <span className={`badge ${attempt.result === "Fail" ? "bg-danger" : "bg-success"}`}>
                              {attempt.result}
                            </span>
                          </td>
                          <td className="text-center">
                            <Link to={`/quiz-detail/${attempt.id}`} className="btn btn-outline-warning btn-sm">
                              Details
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="pagination-container">
                  <div className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizAttemptsPage;